import React, { useCallback, useEffect, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import IconButton from "../components/IconButton";
import { DownloadIcon } from "../components/icons";
import { useElementSize } from "../player/controls";
import { trackEvent } from "../../analytics";

// Same worker as the legacy PDFViewer (must match the installed pdfjs-dist).
GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

/*
 * Source-text pane (player redesign, 2026-10 — "source-text split"): a lean
 * pdf.js viewer styled like the player card. Navy frame, white pages rendered
 * fit-to-width and scrolling inside the pane, and a slim bar of borderless
 * 44px controls: page indicator · zoom − / + · rotate · download · full
 * screen · close (rotate matters: some sources are sideways scans).
 * Pages render lazily as they near the viewport and re-render (debounced)
 * when the pane's width or zoom changes, so opening/closing the split just
 * re-fits. Replaces the legacy ReuseableComponents/PDFViewer on redesigned
 * pages only. Height comes from the caller (locked to the player card).
 */

const ZOOMS = [0.75, 1, 1.25, 1.5, 2, 3];
const PAGE_GAP = 8;
const PAD = 12;

// One fetch per PDF per session — reopening the pane or another class page
// with the same source reuses the parsed document.
const pdfCache = new Map();
function loadPdf(url) {
  if (!pdfCache.has(url)) {
    const promise = getDocument(url).promise;
    promise.catch(() => pdfCache.delete(url));
    pdfCache.set(url, promise);
  }
  return pdfCache.get(url);
}

// Sideways scans need rotating every time — remember the turn per PDF.
const rotationKey = (url) => `ic-source-rotation:${url}`;
const readRotation = (url) => {
  try {
    const n = Number(localStorage.getItem(rotationKey(url)));
    return [90, 180, 270].includes(n) ? n : 0;
  } catch (_) {
    return 0;
  }
};

function PdfPage({ pdf, pageNumber, width, height, rotation, rootRef, registerRef }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rendered = useRef("");
  const taskRef = useRef(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    registerRef(pageNumber, wrapRef.current);
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), {
      root: rootRef.current,
      rootMargin: "400px 0px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, [pageNumber, registerRef, rootRef]);

  // Render when near the viewport and the target width changed; debounced so
  // a width animation doesn't trigger a render per frame (the canvas is CSS-
  // scaled in the meantime).
  useEffect(() => {
    const key = `${width}:${rotation}`;
    if (!near || !width || rendered.current === key) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const turn = (page.rotate + rotation) % 360;
        const base = page.getViewport({ scale: 1, rotation: turn });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: (width / base.width) * dpr, rotation: turn });
        const canvas = canvasRef.current;
        if (!canvas) return;
        taskRef.current?.cancel();
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
        taskRef.current = task;
        await task.promise;
        if (!cancelled) rendered.current = key;
      } catch (_) {
        // RenderingCancelledException on rapid re-fits — the next pass renders.
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [near, width, rotation, pdf, pageNumber]);

  useEffect(() => () => taskRef.current?.cancel(), []);

  return (
    <div ref={wrapRef} className="bg-white shadow-ic-raised-dark flex-shrink-0" style={{ width, height }}>
      <canvas ref={canvasRef} aria-hidden="true" style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}

const Glyph = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export default function SourceViewer({ file, height, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [pageSizes, setPageSizes] = useState([]);
  const [status, setStatus] = useState("loading");
  const [zoomIndex, setZoomIndex] = useState(ZOOMS.indexOf(1));
  const [rotation, setRotation] = useState(() => readRotation(file.url));
  useEffect(() => setRotation(readRotation(file.url)), [file.url]);
  const rotate = () =>
    setRotation((r) => {
      const next = (r + 90) % 360;
      try {
        if (next) localStorage.setItem(rotationKey(file.url), String(next));
        else localStorage.removeItem(rotationKey(file.url));
      } catch (_) {}
      return next;
    });
  const [currentPage, setCurrentPage] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const scrollRef = useRef(null);
  const pageEls = useRef({});
  const { width: paneWidth } = useElementSize(scrollRef);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    loadPdf(file.url)
      .then(async (doc) => {
        const sizes = [];
        for (let n = 1; n <= doc.numPages; n++) {
          const vp = (await doc.getPage(n)).getViewport({ scale: 1 });
          sizes.push({ w: vp.width, h: vp.height });
        }
        if (cancelled) return;
        setPdf(doc);
        setPageSizes(sizes);
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
  }, [file.url]);

  // Full screen: Escape exits (without closing anything around it); page scroll locked.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setFullscreen(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const registerRef = useCallback((n, el) => {
    pageEls.current[n] = el;
  }, []);

  const onScroll = () => {
    const root = scrollRef.current;
    if (!root) return;
    const probe = root.scrollTop + root.clientHeight / 3;
    let page = 1;
    for (let n = 1; n <= pageSizes.length; n++) {
      const el = pageEls.current[n];
      if (el && el.offsetTop <= probe) page = n;
    }
    setCurrentPage(page);
  };

  const zoom = ZOOMS[zoomIndex];
  // clientWidth excludes the (gutter-reserved) scrollbar, so fit-width never overflows.
  const innerWidth = paneWidth > 0 && scrollRef.current ? scrollRef.current.clientWidth : 0;
  const fitWidth = Math.max(0, innerWidth - PAD * 2);
  const pageWidth = Math.round(fitWidth * zoom);

  const frameClasses = fullscreen
    ? "fixed inset-0 z-[80] bg-ic-navy flex flex-col"
    : "relative bg-ic-navy rounded-xl shadow-ic-raised-dark flex flex-col overflow-hidden";

  return (
    <div
      role="region"
      aria-label={`Source text: ${file.label}`}
      className={`ic-scope font-sans ${frameClasses}`}
      style={fullscreen ? undefined : { height }}
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-auto bg-[#0D1420]"
        style={{ padding: PAD, scrollbarGutter: "stable" }}
      >
        {status === "loading" && (
          <div className="h-full flex items-center justify-center text-[14px] text-ic-lightGray">Loading source text…</div>
        )}
        {status === "error" && (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-4">
            <span className="text-[15px] font-semibold text-white">Couldn't load the source text.</span>
            <a href={file.downloadUrl} target="_blank" rel="noreferrer" className="text-[14px] font-semibold text-ic-sky min-h-[44px] inline-flex items-center hover:underline">
              Download the PDF instead
            </a>
          </div>
        )}
        {status === "ready" && pageWidth > 0 && (
          <div className="flex flex-col items-center" style={{ gap: PAGE_GAP, width: Math.max(pageWidth, fitWidth) }}>
            {pageSizes.map((size, i) => {
              const sideways = rotation % 180 !== 0;
              const [w, h] = sideways ? [size.h, size.w] : [size.w, size.h];
              return (
              <PdfPage
                key={i + 1}
                pdf={pdf}
                pageNumber={i + 1}
                width={pageWidth}
                height={Math.round((pageWidth * h) / w)}
                rotation={rotation}
                rootRef={scrollRef}
                registerRef={registerRef}
              />
              );
            })}
          </div>
        )}
      </div>

      {/* Bar — same borderless controls as the player */}
      <div className="flex items-center px-2 py-1 border-t border-white/10">
        <span className="pl-2 text-[13px] leading-4 text-ic-lightGray tabular-nums whitespace-nowrap" title={file.label}>
          {status === "ready" ? (
            <>
              {currentPage}
              <span className="text-white/40"> / </span>
              {pageSizes.length}
            </>
          ) : (
            "Source text"
          )}
        </span>
        <div className="flex-1" />
        <IconButton
          variant="bareDark"
          size={44}
          dark
          label="Zoom out"
          disabled={zoomIndex === 0}
          className="disabled:opacity-40 disabled:hover:bg-transparent"
          onClick={() => setZoomIndex((z) => Math.max(0, z - 1))}
        >
          <Glyph>
            <path d="M5 12h14" />
          </Glyph>
        </IconButton>
        <IconButton
          variant="bareDark"
          size={44}
          dark
          label="Zoom in"
          disabled={zoomIndex === ZOOMS.length - 1}
          className="disabled:opacity-40 disabled:hover:bg-transparent"
          onClick={() => setZoomIndex((z) => Math.min(ZOOMS.length - 1, z + 1))}
        >
          <Glyph>
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </Glyph>
        </IconButton>
        <IconButton variant="bareDark" size={44} dark label="Rotate pages" onClick={rotate}>
          <Glyph>
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 3v6h-6" />
          </Glyph>
        </IconButton>
        <a
          href={file.downloadUrl}
          download
          target="_blank"
          rel="noreferrer"
          aria-label="Download source text PDF"
          onClick={() => {
            try {
              trackEvent({ category: "player", action: "download", label: `pdf:${file.label}` });
            } catch (_) {}
          }}
          className="w-11 h-11 rounded-full text-ic-lightGray inline-flex items-center justify-center transition-all duration-150 ease-out hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
        >
          <DownloadIcon size={20} />
        </a>
        <IconButton
          variant="bareDark"
          size={44}
          dark
          label={fullscreen ? "Exit full screen" : "Full screen"}
          aria-pressed={fullscreen}
          onClick={() => setFullscreen((f) => !f)}
        >
          <Glyph>
            {fullscreen ? (
              <>
                <path d="M9 4v5H4" />
                <path d="M15 4v5h5" />
                <path d="M9 20v-5H4" />
                <path d="M15 20v-5h5" />
              </>
            ) : (
              <>
                <path d="M4 9V4h5" />
                <path d="M20 9V4h-5" />
                <path d="M4 15v5h5" />
                <path d="M20 15v5h-5" />
              </>
            )}
          </Glyph>
        </IconButton>
        <IconButton
          variant="bareDark"
          size={44}
          dark
          label="Close source text"
          onClick={() => {
            setFullscreen(false);
            onClose();
          }}
        >
          <Glyph>
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </Glyph>
        </IconButton>
      </div>
    </div>
  );
}
